/* Northsheep — the click-to-zoom view, one picture at a time.

   Dawn's product modal stacks every image in a tall scrolling column. On a
   product where the photographs ARE the product that is the wrong shape: you
   land in the middle of a 2000px image with no idea how many more there are,
   and reading them means scrolling past each one.

   This turns the same markup into a horizontal track — swipe on a phone,
   arrows or arrow keys on a desktop, and a count so you know where you are.
   No library: the track is a scroll container with scroll-snap, so the swipe
   is the browser's own and costs nothing.

   Dawn's own modal code is untouched, but it cannot be trusted to land on
   the right picture: its showActiveMedia ends by setting scrollLeft from the
   image's intrinsic width, which against full-width slides lands inside
   slide one. So the opening position is set again here, from the opener's own
   media id — see alignToOpener.

   Revert: Theme settings -> Northsheep product page -> uncheck "Swipe through
   the zoomed images". The stylesheet and this file then do nothing. */
(function () {
  'use strict';

  var SELECTOR = 'product-modal';

  function track(modal) {
    return modal.querySelector('[role="document"]');
  }

  function items(modal) {
    return Array.prototype.slice.call(modal.querySelectorAll('[data-media-id]'));
  }

  /* Which picture is on screen. Measured from the track's own scroll position
     rather than tracked in a variable, so a swipe, an arrow press and Dawn's
     opening scrollIntoView all agree without talking to each other. */
  function currentIndex(modal) {
    var t = track(modal);
    var list = items(modal);
    if (!t || !list.length) return 0;
    var mid = t.scrollLeft + t.clientWidth / 2;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      var centre = el.offsetLeft + el.offsetWidth / 2;
      var d = Math.abs(centre - mid);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  /* WHERE THE NEXT STEP STARTS FROM.

     currentIndex reads the live scroll position, which is the right answer
     for a swipe and the wrong one during a smooth scroll: clicking "next"
     twice quickly measured the track still mid-flight between 2 and 3, so
     the second click asked for 3 again and appeared to do nothing. The
     pending destination is remembered and used instead, and dropped once the
     track has actually arrived. */
  function baseIndex(modal) {
    return typeof modal.__nsTarget === 'number' ? modal.__nsTarget : currentIndex(modal);
  }

  function goTo(modal, index) {
    var list = items(modal);
    if (!list.length) return;
    var i = Math.max(0, Math.min(list.length - 1, index));
    modal.__nsTarget = i;
    var t = track(modal);
    var el = list[i];
    /* scrollTo, not scrollIntoView: scrollIntoView on the inline axis also
       scrolls the PAGE behind the modal on some browsers, which left the
       product page halfway down the recommendations after closing. */
    t.scrollTo({ left: el.offsetLeft, behavior: 'smooth' });
    paint(modal, i);
  }

  function paint(modal, index) {
    var list = items(modal);
    var i = typeof index === 'number' ? index : currentIndex(modal);
    var counter = modal.querySelector('[data-ns-zoom-count]');
    if (counter) counter.textContent = i + 1 + ' / ' + list.length;
    var prev = modal.querySelector('[data-ns-zoom-prev]');
    var next = modal.querySelector('[data-ns-zoom-next]');
    /* Disabled rather than hidden: a control that vanishes moves the other
       one, and at the ends that is exactly when a finger is already on it. */
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= list.length - 1;
  }

  /* LAND ON THE PICTURE THAT WAS CLICKED.

     Dawn's own showActiveMedia ends with
       container.scrollLeft = (activeMedia.width - container.clientWidth) / 2
     where `width` is the image's intrinsic width. It is a leftover from the
     stacked layout, and against a track of full-width slides it computes a
     position a couple of hundred pixels into slide one — so opening from the
     third thumbnail showed the first picture. Measured: "1 / 5" after
     clicking thumbnail 3.

     So the position is set again from the opener's own media id, after
     Dawn has had its turn. No smooth scroll: the modal is still appearing,
     and animating from a wrong position only shows the wrong position. */
  function alignToOpener(modal) {
    var list = items(modal);
    if (!list.length) return;
    var opener = modal.openedBy;
    var id = opener && opener.getAttribute ? opener.getAttribute('data-media-id') : null;
    var i = 0;
    if (id) {
      for (var k = 0; k < list.length; k++) {
        if (list[k].getAttribute('data-media-id') === id) {
          i = k;
          break;
        }
      }
    }
    delete modal.__nsTarget;
    track(modal).scrollLeft = list[i].offsetLeft;
    paint(modal, i);
  }

  function watchOpen(modal) {
    /* One per window (audit E11, 2 Oct 2026): every variant change rebuilt a
       one-photo window and added another watcher. */
    if (modal.__nsOpenWatch) return;
    modal.__nsOpenWatch = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].attributeName !== 'open') continue;
        if (!modal.hasAttribute('open')) continue;
        /* setTimeout, not rAF: show() is synchronous and sets the attribute
           BEFORE it scrolls, so this has to run after the whole call
           finishes — and a hidden tab never fires rAF at all. */
        setTimeout(function () {
          alignToOpener(modal);
        }, 0);
      }
    });
    modal.__nsOpenWatch.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  function build(modal) {
    if (modal.dataset.nsZoomNav === 'ready') return;
    var list = items(modal);
    var dialog = modal.querySelector('.product-media-modal__dialog');
    if (!dialog || list.length < 2) {
      /* One picture needs no navigation, and marking it done stops the
         observer from trying again on every mutation. It still gets the
         alignment, or Dawn's leftover scrollLeft offsets the only image. */
      modal.dataset.nsZoomNav = 'ready';
      if (list.length === 1) watchOpen(modal);
      return;
    }

    var nav = document.createElement('div');
    nav.className = 'ns-zoom-nav';
    nav.innerHTML =
      '<button type="button" class="ns-zoom-nav__btn ns-zoom-nav__btn--prev" data-ns-zoom-prev aria-label="Previous image"></button>' +
      '<span class="ns-zoom-nav__count" data-ns-zoom-count aria-live="polite"></span>' +
      '<button type="button" class="ns-zoom-nav__btn ns-zoom-nav__btn--next" data-ns-zoom-next aria-label="Next image"></button>';
    /* The store's own words (audit, 2 Oct 2026): the same labels as the arrows of
       the gallery behind the modal. The English above stays as the fallback. */
    var tPrev = modal.getAttribute('data-ns-t-prev'), tNext = modal.getAttribute('data-ns-t-next');
    if (tPrev) nav.querySelector('[data-ns-zoom-prev]').setAttribute('aria-label', tPrev);
    if (tNext) nav.querySelector('[data-ns-zoom-next]').setAttribute('aria-label', tNext);
    dialog.appendChild(nav);

    /* THE ARROWS MUST NOT REACH THE MODAL, AND `click` IS THE WRONG EVENT.

       ModalDialog closes a .media-modal on POINTERUP:

         this.addEventListener('pointerup', (event) => {
           if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model'))
             this.hide();
         });

       So any mouse release anywhere inside the modal closes it, arrows
       included, and stopping `click` was useless — the modal had already been
       hidden by the time click fired. Pointer events are stopped here
       instead. (This is also why the first fix tested green and failed in the
       browser: a scripted .click() dispatches no pointer events at all, so
       the test could not see the bug. Verified with a real mouse click.) */
    ['pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach(function (type) {
      nav.addEventListener(type, function (e) {
        e.stopPropagation();
      });
    });
    nav.querySelector('[data-ns-zoom-prev]').addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      goTo(modal, baseIndex(modal) - 1);
    });
    nav.querySelector('[data-ns-zoom-next]').addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      goTo(modal, baseIndex(modal) + 1);
    });

    var t = track(modal);
    var raf = 0;
    t.addEventListener(
      'scroll',
      function () {
        /* The count follows a swipe, so it must not repaint on every scroll
           event — one per frame is enough and keeps the swipe smooth. */
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var now = currentIndex(modal);
          if (modal.__nsTarget === now) delete modal.__nsTarget;
          paint(modal, typeof modal.__nsTarget === 'number' ? modal.__nsTarget : now);
        });
      },
      { passive: true }
    );

    /* THE WHEEL MOVES THROUGH THE PICTURES.

       A vertical wheel over a horizontal track does nothing by itself, which
       left the obvious gesture dead: people scroll to see more pictures. Both
       axes are accepted, since a trackpad's horizontal swipe belongs here too.

       Not passive, because the default has to be prevented — otherwise the
       page behind scrolls instead, which is what it used to do.

       An accumulator with a short lock afterwards, because one flick of a
       trackpad fires a dozen small deltas: without it a single gesture flew
       through every picture to the end. 40 is below one mouse notch (~100) so
       a real wheel still steps once per click. */
    var accum = 0;
    var lockUntil = 0;
    modal.addEventListener(
      'wheel',
      function (e) {
        if (!modal.hasAttribute('open')) return;
        /* ctrl+wheel is the browser's own zoom. Taking that away from someone
           trying to look closer at a hide would be the opposite of helpful. */
        if (e.ctrlKey) return;
        e.preventDefault();
        if (Date.now() < lockUntil) return;
        accum += Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
        if (Math.abs(accum) < 40) return;
        var dir = accum > 0 ? 1 : -1;
        accum = 0;
        lockUntil = Date.now() + 320;
        goTo(modal, baseIndex(modal) + dir);
      },
      { passive: false }
    );

    modal.dataset.nsZoomNav = 'ready';
    watchOpen(modal);
    paint(modal);
  }

  /* Arrow keys, but only while the modal is actually open — Dawn marks that
     with the `open` attribute. Escape stays Dawn's to handle.

     ONCE PER PAGE, however often this file runs (audit, 23 Sep 2026). A
     colour swap (ns-colour-siblings.js) replaces <main> and runs this file
     again — which is wanted, start() below builds the new zoom — but every run
     also added another of these, and each one moves a picture: after one swap
     a single press skipped two photos, after two swaps three. It looks the
     modal up on every press, so the first one serves every later page. */
  if (!window.__nsZoomNavKeys) {
    window.__nsZoomNavKeys = true;
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var modal = document.querySelector(SELECTOR + '[open]');
      if (!modal) return;
      e.preventDefault();
      goTo(modal, baseIndex(modal) + (e.key === 'ArrowRight' ? 1 : -1));
    });
  }

  function start() {
    document.querySelectorAll(SELECTOR).forEach(build);
    /* The modal is re-rendered when a variant changes the media list, so the
       nav has to be rebuilt. Debounced per the house convention: a variant
       switch replaces the whole list and fires a burst of mutations. */
    var pending = 0;
    var obs = new MutationObserver(function (records) {
      /* NOT ITS OWN WRITES (audit, 23 Sep 2026). paint() sets the counter's
         text, which is a mutation inside the modal, which ran this, which
         painted again: measured 11 rewrites a second, for as long as the page
         was open, zoom closed or not — a timer and a layout read every 90ms,
         through every fling of the gallery. Only a change OUTSIDE the arrows
         (Dawn re-rendering the photo list) is a reason to look again. */
      var foreign = false;
      for (var r = 0; r < records.length; r++) {
        var node = records[r].target;
        var el = node.nodeType === 1 ? node : node.parentElement;
        if (!el || !el.closest('.ns-zoom-nav')) {
          foreign = true;
          break;
        }
      }
      if (!foreign) return;
      if (pending) clearTimeout(pending);
      pending = setTimeout(function () {
        pending = 0;
        document.querySelectorAll(SELECTOR).forEach(function (m) {
          if (!m.querySelector('.ns-zoom-nav')) {
            delete m.dataset.nsZoomNav;
            build(m);
          } else {
            paint(m);
          }
        });
      }, 60);
    });
    document.querySelectorAll(SELECTOR).forEach(function (m) {
      obs.observe(m, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
